import type { PackageSection } from './types';

/** Vaste volgorde waarin secties getoond worden; bepaalt ook de globale sort_order. */
export const SECTION_ORDER: PackageSection[] = ['verpakking', 'gadget', 'drank', 'food', 'overig'];

/** Per-sectie lijst van item-ids in visuele volgorde (dnd-kit multi-container state). */
export type Containers = Record<PackageSection, string[]>;

export function emptyContainers(): Containers {
  return { verpakking: [], gadget: [], drank: [], food: [], overig: [] };
}

function isSectionId(id: string): id is PackageSection {
  return (SECTION_ORDER as string[]).includes(id);
}

/** Vind in welke sectie een item-id zit (of de sectie zelf als id een sectie is). */
export function findContainer(containers: Containers, id: string): PackageSection | null {
  if (isSectionId(id)) return id;
  for (const s of SECTION_ORDER) {
    if (containers[s].includes(id)) return s;
  }
  return null;
}

/**
 * Verplaats `activeId` naar de positie van `overId` (item of sectie-container).
 * Werkt binnen dezelfde sectie (herordenen) en tussen secties.
 * Pure functie: muteert de input niet.
 */
export function moveItem(containers: Containers, activeId: string, overId: string): Containers {
  const from = findContainer(containers, activeId);
  const to = findContainer(containers, overId);
  if (!from || !to || activeId === overId) return containers;

  const next = emptyContainers();
  for (const s of SECTION_ORDER) next[s] = [...containers[s]];

  next[from] = next[from].filter((i) => i !== activeId);

  let index: number;
  if (isSectionId(overId)) {
    index = next[to].length; // gedropt op (lege) sectie-container -> achteraan
  } else {
    const overIdx = next[to].indexOf(overId);
    index = overIdx < 0 ? next[to].length : overIdx;
  }
  next[to].splice(index, 0, activeId);
  return next;
}

export interface ReorderUpdate {
  id: string;
  sort_order: number;
  /** NULL als de visuele sectie gelijk is aan de natuurlijke (product-afgeleide) sectie. */
  section_override: PackageSection | null;
}

/**
 * Zet de container-state om naar DB-updates: globale sort_order (0..n over secties heen)
 * plus section_override (alleen gezet als de visuele sectie afwijkt van de natuurlijke).
 */
export function toUpdates(
  containers: Containers,
  naturalSection: Record<string, PackageSection>
): ReorderUpdate[] {
  const updates: ReorderUpdate[] = [];
  let order = 0;
  for (const section of SECTION_ORDER) {
    for (const id of containers[section]) {
      updates.push({
        id,
        sort_order: order,
        section_override: naturalSection[id] === section ? null : section,
      });
      order += 1;
    }
  }
  return updates;
}
